import { ContextConsumer, createContext } from "@lit/context";
import { LitElement } from "lit";
import { Engine } from "../../engine/Engine";
import { SugarModel } from "../../loader/SugarModel";
import {
  setConfiguratorData,
  CONFIGURATOR_MODEL_TYPE,
  MODEL_PART_TYPE,
} from "../../core/configurator/ConfiguratorData";
import { initializeConfiguratorLocale } from "./SugarConfiguratorContextServices";
import {
  ConfiguratorDocumentController,
  type ConfiguratorHistoryState,
} from "./controllers/ConfiguratorDocumentController";
import { ConfiguratorObservableState } from "./controllers/ConfiguratorObservableState";
import { ConfiguratorSelectionController } from "./controllers/ConfiguratorSelectionController";
import {
  ConfiguratorSessionController,
  type ConfiguratorMode,
} from "./controllers/ConfiguratorSessionController";
import { applyViewportLifecycleProfile } from "../../core/viewport/ViewportLifecycleProfiles";
import type {
  ConfiguratorCommandMap,
  ConfiguratorEventMap,
  ConfiguratorStoreMap,
} from "./ConfiguratorMessageContracts";
import { ProductMessaging } from "../../core/events/messaging/ProductMessaging";

export type { ConfiguratorHistoryState };

export const configuratorContext = createContext<SugarConfiguratorContext>(
  "configuratorContext",
);

/** Legacy observeContext property names → messaging store keys / handler args. */
type ObserveProperty =
  | "model"
  | "refresh"
  | "scene"
  | "selectedModel"
  | "historyState";

export class SugarConfiguratorContext {
  private _locale = "TR";
  readonly messages = new ProductMessaging<
    ConfiguratorCommandMap,
    ConfiguratorEventMap,
    ConfiguratorStoreMap
  >({
    session: { started: false, mode: "configurator" },
    orbit: { enabled: true },
    dragPreview: { phase: "idle" },
    selection: {},
    dimensions: {},
    history: { canUndo: false, canRedo: false, restoring: false },
    model: {},
    scene: {},
    ui: { refreshTick: 0 },
  });
  private readonly _session = new ConfiguratorSessionController((state) => {
    this.messages.store("dragPreview").set(state);
    this.messages.emit("drag-preview.changed", state);
  });
  private readonly _state = new ConfiguratorObservableState({
    onModel: (model) => this.messages.store("model").set({ value: model }),
    onScene: (scene) => this.messages.store("scene").set({ value: scene }),
    onSelectedModel: (model) => {
      this.messages.store("selection").set({ model });
      this.messages.emit("selection.changed", model);
    },
    onRefresh: () =>
      this.messages.store("ui").update((state) => ({
        refreshTick: state.refreshTick + 1,
      })),
  });
  private readonly _document = new ConfiguratorDocumentController((state) => {
    this.messages.store("history").set(state);
    this.messages.emit("history.changed", state);
  });
  private readonly _selection = new ConfiguratorSelectionController(
    (model) => {
      this._state.setSelectedModel(model);
    },
    () => this._state.refresh(),
    () => this._session.configuratorController,
  );

  constructor(_element: HTMLElement) {
    void _element;
    this.registerCommands();
    initializeConfiguratorLocale();
  }

  start(
    bindElement: HTMLElement,
    type: "editor" | "configurator" = "editor",
    engine?: Engine,
  ): void {
    const sessionEngine = engine ?? new Engine();
    if (!engine) {
      sessionEngine.bindElement(bindElement);
      sessionEngine.start();
    }
    applyViewportLifecycleProfile(sessionEngine, "configurator");
    const controller = this.messages.execute("session.start", {
      bindElement,
      mode: type,
      engine: sessionEngine,
    });
    this._state.bindEngine(sessionEngine);
    this._selection.bind(sessionEngine, bindElement);
    this._document.bind(controller);
  }

  set configuratorId(value: string) {
    this._session.id = value;
  }

  get configuratorId(): string {
    return this._session.id;
  }

  set refresh(_value: unknown) {
    this._state.refresh();
  }

  set model(value: CONFIGURATOR_MODEL_TYPE) {
    setConfiguratorData(this.configuratorId, value);
    this._state.setModel(value);
  }

  get model(): CONFIGURATOR_MODEL_TYPE {
    return this._state.model!;
  }

  get engine(): Engine | undefined {
    return this._session.engine;
  }

  get historyState(): ConfiguratorHistoryState {
    return this._document.state;
  }

  undo(): Promise<void> {
    return this.messages.execute("history.undo", undefined);
  }

  redo(): Promise<void> {
    return this.messages.execute("history.redo", undefined);
  }

  set mode(value: ConfiguratorMode) {
    this.messages.execute("session.mode.set", value);
  }

  set scene(value: unknown) {
    this._state.setScene(value);
  }

  get scene() {
    return this._state.scene;
  }

  set selectedModel(value: SugarModel | undefined) {
    this._state.setSelectedModel(value);
  }

  get selectedModel(): SugarModel | undefined {
    return this._state.selectedModel;
  }

  async changeMaterial(materialConfig: any[]) {
    await this.messages.execute("material.change", materialConfig);
  }

  async changePart(partConfig: MODEL_PART_TYPE) {
    await this.messages.execute("part.change", partConfig);
  }

  async changeSidePart(sideConfig: MODEL_PART_TYPE) {
    await this.messages.execute("side-part.change", sideConfig);
  }

  async setTopViewForRender(): Promise<void> {
    await this.messages.execute("camera.render-view.prepare", undefined);
  }

  async takeScreenshot(): Promise<string | null> {
    return this._session.takeScreenshot();
  }

  async takeThumbnnail(): Promise<string | null> {
    return this._session.takeThumbnnail();
  }

  async clearAll() {
    await this.messages.execute("scene.clear", undefined);
  }

  dispose(): void {
    this._document.dispose();
    this._selection.dispose();
    this._state.dispose();
    this._session.dispose();
    this.messages.dispose();
  }

  showTopView() {
    this.messages.execute("camera.top-view.show", undefined);
  }

  showDimensions() {
    this.messages.execute("dimensions.toggle", undefined);
  }

  changeMode(mode: any) {
    this.mode = mode;
  }

  get products() {
    return this._session.products;
  }

  validateConfigurations() {
    return this._session.validateConfigurations();
  }

  beginPaletteDrag(event: DragEvent, model?: unknown) {
    return this._session.beginPaletteDrag(event, model);
  }

  cancelPaletteDrag(): void {
    this._session.cancelPaletteDrag();
  }

  private registerCommands(): void {
    this.messages.register("session.start", ({ bindElement, mode, engine }) => {
      void bindElement;
      const sessionEngine = engine ?? new Engine();
      const controller = this._session.start(sessionEngine, mode);
      this.messages.store("session").set({ started: true, mode });
      this.messages.store("orbit").set({ enabled: true });
      this.messages.emit("session.started", {
        engine: sessionEngine,
        controller,
      });
      return controller;
    });
    this.messages.register("session.mode.set", (mode) => {
      this._session.mode = mode;
      this.messages.store("session").update((state) => ({ ...state, mode }));
    });
    this.messages.register("material.change", (config) =>
      this._session.changeMaterial(config),
    );
    this.messages.register("part.change", (config) =>
      this._session.changePart(config),
    );
    this.messages.register("side-part.change", (config) =>
      this._session.changeSidePart(config),
    );
    this.messages.register("scene.clear", () => this._session.clearAll());
    this.messages.register("camera.top-view.show", () => {
      this._session.showTopView();
    });
    this.messages.register("camera.render-view.prepare", () =>
      this._session.setTopViewForRender(),
    );
    this.messages.register("dimensions.toggle", () => {
      this._session.showDimensions();
      this.messages.store("dimensions").update((state) => ({
        visible: state.visible === undefined ? true : !state.visible,
      }));
      this.messages.emit(
        "dimensions.changed",
        this.messages.store("dimensions").snapshot,
      );
    });
    this.messages.register("history.undo", () => this._document.undo());
    this.messages.register("history.redo", () => this._document.redo());
  }
}

function readObserveValue(
  context: SugarConfiguratorContext,
  property: ObserveProperty,
): unknown {
  switch (property) {
    case "model":
      return context.messages.store("model").snapshot.value;
    case "refresh":
      return context.messages.store("ui").snapshot.refreshTick;
    case "scene":
      return context.messages.store("scene").snapshot.value;
    case "selectedModel":
      return context.messages.store("selection").snapshot.model;
    case "historyState":
      return context.messages.store("history").snapshot;
  }
}

function subscribeObserveProperty(
  context: SugarConfiguratorContext,
  property: ObserveProperty,
  listener: () => void,
): () => void {
  switch (property) {
    case "model":
      return context.messages.store("model").subscribe(() => listener());
    case "refresh":
      return context.messages.store("ui").subscribe(() => listener());
    case "scene":
      return context.messages.store("scene").subscribe(() => listener());
    case "selectedModel":
      return context.messages.store("selection").subscribe(() => listener());
    case "historyState":
      return context.messages.store("history").subscribe(() => listener());
  }
}

/**
 * Binds Lit UI to configurator messaging stores (single channel).
 * Keeps onXChanged handler names for existing panels.
 */
export function observeContext(...properties: ObserveProperty[]) {
  const onProperyChangedNames = properties.map(
    (property) =>
      `on${property.charAt(0).toUpperCase() + property.slice(1)}Changed`,
  );

  return function <T extends { new (...args: any[]): LitElement }>(Base: T) {
    return class extends Base {
      private __contextConsumer = new ContextConsumer(this, {
        context: configuratorContext,
        callback: (value) => {
          if ((this as any)["onContextChanged"]) {
            (this as any)["onContextChanged"](value);
          }
          this.__context = value;
          this.__setupStoreSubscriptions();
          properties.forEach((property, i) => {
            const current = readObserveValue(value, property);
            if (current !== undefined) {
              this.__onContextChange(onProperyChangedNames[i], property);
            }
          });
        },
      });

      __context?: SugarConfiguratorContext;
      private __storeUnsubs: Array<() => void> = [];

      private __setupStoreSubscriptions() {
        for (const unsub of this.__storeUnsubs) unsub();
        this.__storeUnsubs = [];
        if (!this.__context) return;
        properties.forEach((property, i) => {
          const unsub = subscribeObserveProperty(this.__context!, property, () =>
            this.__onContextChange(onProperyChangedNames[i], property),
          );
          this.__storeUnsubs.push(unsub);
        });
      }

      private __onContextChange = (
        onProperyChanged: string,
        property: ObserveProperty,
      ) => {
        if (!(this as any)[onProperyChanged] || !this.__context) return;
        (this as any)[onProperyChanged](
          readObserveValue(this.__context, property),
        );
      };

      connectedCallback() {
        super.connectedCallback?.();
        this.__setupStoreSubscriptions();
      }

      disconnectedCallback() {
        super.disconnectedCallback?.();
        for (const unsub of this.__storeUnsubs) unsub();
        this.__storeUnsubs = [];
      }

      protected get context(): SugarConfiguratorContext | undefined {
        return this.__context;
      }
    };
  };
}

export function injectContext(): PropertyDecorator {
  return (proto: any, key: string | symbol) => {
    Object.defineProperty(proto, key, {
      get() {
        return configuratorContext;
      },
      enumerable: true,
      configurable: true,
    });
  };
}

interface WithEventHandlerTracking {
  __eventHandlers?: {
    type: string;
    handler: EventListener;
    target: EventTarget;
  }[];
}

type HTMLElementWithLifecycle = HTMLElement &
  Partial<WithEventHandlerTracking> & {
    connectedCallback?(): void;
    disconnectedCallback?(): void;
  };

export function onEvent<K extends keyof HTMLElementEventMap>(
  type: K,
  options?: { target?: EventTarget },
): MethodDecorator {
  return function (
    target: Object,
    key: string | symbol,
    _descriptor: PropertyDescriptor,
  ) {
    const connected = (target as HTMLElementWithLifecycle).connectedCallback;
    const disconnected = (target as HTMLElementWithLifecycle)
      .disconnectedCallback;

    (target as HTMLElementWithLifecycle).connectedCallback = function () {
      const self = this as HTMLElementWithLifecycle;
      const handler = (e: Event) => (self as any)[key](e);
      const eventTarget = options?.target ?? self;

      eventTarget.addEventListener(type, handler);

      self.__eventHandlers ??= [];
      self.__eventHandlers.push({ type, handler, target: eventTarget });

      connected?.call(self);
    };

    (target as HTMLElementWithLifecycle).disconnectedCallback = function () {
      const self = this as HTMLElementWithLifecycle;
      for (const { type, handler, target } of self.__eventHandlers ?? []) {
        target.removeEventListener(type, handler);
      }
      self.__eventHandlers = [];

      disconnected?.call(self);
    };
  };
}
